// v2 oak — an ancient field oak: a stout flared trunk with tetrahedron root
// buttresses that forks into two heavy limbs and a stub, carrying a broad,
// layered crown (dark under-canopy shadow, a wide ring of four flattened lobes,
// a main dome and a sunlit top cap). Each variant re-spins the buttresses, the
// lobe ring, the trunk lean and the palette deterministically. Ported 1:1 from
// age-of-mcp's v2/tree-oak.ts, using the library's shared `tree` helpers.
import * as THREE from "three";
import { defineModel, tree, type Part } from "../../lib";

const PALETTES = [
  { deep: "#2e5e28", mid: "#3f7d33", lite: "#549540", top: "#69ad4e" },
  { deep: "#2a5a30", mid: "#3a763b", lite: "#4c8f46", top: "#5fa551" },
  { deep: "#39632a", mid: "#4b8033", lite: "#5f9a3e", top: "#75b04b" },
] as const;

const BARK_DARK = "#46331f";
const BARK = "#5d452c";

function oakParts(v: number): Part[] {
  const seed = 31 + v * 7;
  const yaw = v * 2.1;
  const lean = (tree.h01(seed, 0) - 0.5) * 0.14;
  const pal = tree.pick(PALETTES, v);
  const parts: Part[] = [
    // flared root collar + stout trunk with a slight lean
    { geometry: new THREE.CylinderGeometry(0.17, 0.24, 0.16, 7),
      color: BARK_DARK, position: [0, 0.08, 0] },
    { geometry: new THREE.CylinderGeometry(0.1, 0.16, 0.62, 7),
      color: BARK, position: [0, 0.42, 0], rotation: [0, 0, lean] },
    // the fork: two heavy limbs and a short stub reaching into the crown
    { geometry: new THREE.CylinderGeometry(0.045, 0.07, 0.55, 5),
      color: BARK, position: [-0.18, 0.86, 0.08], rotation: [0.28, 0, 0.78] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.07, 0.52, 5),
      color: BARK_DARK, position: [0.2, 0.9, -0.06], rotation: [-0.22, 0, -0.7] },
    { geometry: new THREE.CylinderGeometry(0.035, 0.05, 0.3, 5),
      color: BARK, position: [0.02, 0.95, 0.16], rotation: [0.6, 0, 0.1] },
    // dark under-canopy shadow filling the crown's belly
    tree.lobe(seed, 10, 0.02, 0.95, 0.02, 0.4, pal.deep, 0.55),
  ];
  // root buttresses around the collar
  for (let k = 0; k < 3; k++) {
    const ang = yaw + k * 2.1 + tree.h01(seed, 50 + k) * 0.6;
    parts.push({
      geometry: new THREE.TetrahedronGeometry(0.09, 0),
      color: BARK_DARK,
      position: [Math.cos(ang) * 0.2, 0.05, Math.sin(ang) * 0.2],
      rotation: [0.5, tree.h01(seed, 60 + k) * Math.PI * 2, 0.4],
    });
  }
  // the broad lobe ring — the multi-lobed oak silhouette
  for (let k = 0; k < 4; k++) {
    const ang = yaw + k * (Math.PI / 2) + tree.h01(seed, 20 + k) * 0.5;
    const d = 0.3 + tree.h01(seed, 24 + k) * 0.08;
    const r = 0.31 + tree.h01(seed, 28 + k) * 0.06;
    parts.push(tree.lobe(seed, 32 + k,
      Math.cos(ang) * d, 1.06 + tree.h01(seed, 36 + k) * 0.1, Math.sin(ang) * d,
      r, k % 2 === 0 ? pal.mid : pal.lite, 0.7));
  }
  // main dome + sunlit cap
  parts.push(tree.lobe(seed, 40, 0.02, 1.32, -0.02, 0.36, pal.mid, 0.85));
  parts.push(tree.lobe(seed, 41, 0.08, 1.49, 0.06, 0.2, pal.top, 0.8));
  return parts;
}

export const treeOakV2 = defineModel({
  id: "tree-oak-v2",
  batchId: "fable",
  name: "Tree oak (v2)",
  tags: ["scenery", "tree", "oak", "low-poly", "age-of-mcp", "fable"],
  summary: "Low-poly tree oak.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.58,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => oakParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => oakParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => oakParts(2) },
  ],
  animations: [],
});
